import { useState } from "react";
import { Trash2 } from "lucide-react";
import { L } from "@/i18n";
import { useDeleteQuestions } from "@/hooks/useDuplicates";
import { ConfirmDialog } from "@/components/users/ConfirmDialog";
import { DuplicateItem } from "./DuplicateItem";

const signature = (q) => JSON.stringify([q.options, q.correct_option, q.explanation ?? ""]);

export const DuplicateGroup = ({ group, onEdit }) => {
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const del = useDeleteQuestions();
  const items = group.items ?? [];
  const base = items[0] ? signature(items[0]) : "";
  const full = selected.length >= items.length - 1;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const keepOnly = (id) => setSelected(items.filter((i) => i.id !== id).map((i) => i.id));
  const confirm = () => del.mutate(selected, { onSettled: () => { setConfirmOpen(false); setSelected([]); } });

  return (
    <section className="rounded-lg bg-[#131f37] border border-[#1e293b] p-5" data-testid={`duplicate-group-${group.hash}`}>
      <header className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="text-sm text-slate-100 leading-relaxed" data-testid={`duplicate-group-text-${group.hash}`}>{items[0]?.question_text}</p>
          <p className="mt-1.5 font-mono text-[11px] text-slate-500">
            {L("{n} copies", "{n} cópias", { n: group.count ?? items.length })} · {group.submodule ?? (group.parents ?? []).join(", ")} · #{group.hash.slice(0, 8)}
          </p>
        </div>
        <button
          type="button"
          disabled={!selected.length}
          onClick={() => setConfirmOpen(true)}
          data-testid={`duplicate-delete-button-${group.hash}`}
          className="shrink-0 flex items-center gap-2 rounded-md bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {L("Delete selected ({n})", "Eliminar selecionadas ({n})", { n: selected.length })}
        </button>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((item, i) => (
          <DuplicateItem
            key={item.id}
            item={item}
            differs={i > 0 && signature(item) !== base}
            selected={selected.includes(item.id)}
            disabled={full}
            onToggle={() => toggle(item.id)}
            onKeepOnly={() => keepOnly(item.id)}
            onEdit={onEdit}
          />
        ))}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={L("Delete {n} duplicate question(s)?", "Eliminar {n} pergunta(s) duplicada(s)?", { n: selected.length })}
        description={L(
          "The selected copies will be permanently removed from the catalog. {k} copy(ies) of this question will remain.",
          "As cópias selecionadas serão removidas permanentemente do catálogo. Ficarão {k} cópia(s) desta pergunta.",
          { k: items.length - selected.length },
        )}
        confirmLabel={L("Delete", "Eliminar")}
        danger
        loading={del.isPending}
        onConfirm={confirm}
      />
    </section>
  );
};
